import { PLACE_MENTIONS_PAGE_SIZE } from "@/constants/place"
import { Message } from "@/types/chat"
import { DayObject } from "@/types/date"
import { PlaceRef } from "@/types/entity"
import { UserPosition } from "@/types/geolocation"
import {
  CommunityFeed,
  DetailedPlace,
  EventParams,
  EventSummary,
  LocationSummary,
  PlaceMenu,
  PlacePlaceholder,
  PlaceReview,
  PlaceSummary,
  PlaceType,
  PlaceWeather,
  RelatedPlacePlaceholders,
  Stats,
} from "@/types/place"
import {
  PlaceTypeAheadSearchResult,
  PlaceWebTypeAheadSearchResult,
} from "@/types/place/place-search-input"
import { Route, RouteTravelMode } from "@/types/route"

import { PagingMeta } from "@/lib/paging"
import { baseApi } from "./base-api"

// The backend caps each places request at 50 entity_refs (PlacesController#index)
const PLACES_BY_REF_REQUEST_LIMIT = 50

export interface GetPlaceArgs {
  placeRef: PlaceRef
  eventParams?: EventParams
}

export interface GetDetailedPlaceArgs {
  placeRef: PlaceRef
  eventParams?: EventParams
}

export interface GetEventsArgs {
  placeRef: PlaceRef
  eventParams?: EventParams
}

export interface GetCommunityFeedArgs {
  placeRef: PlaceRef
  page?: number
  perPage?: number
}

export interface PlaceReviewPayload {
  rating?: number
  body?: string
  highlights?: string[]
  photo_urls?: string[]
}

export interface GetUserReviewsArgs {
  handle?: string
  page?: number
  perPage?: number
}

export interface GetDetailedPlaceWithMessageArgs {
  placeRef: PlaceRef
  messageId: Message["id"]
  eventParams?: EventParams
}

export interface GetMapWeatherArgs {
  swLat: number
  swLng: number
  neLat: number
  neLng: number
  zoom: number
}

export interface GetMapReferencePlacesArgs {
  swLat: number
  swLng: number
  neLat: number
  neLng: number
  types?: string[]
  orgid?: string
}

export interface MapReferencePlacesResponse {
  places: PlacePlaceholder[]
}

export interface MapWeatherResponse {
  weather: {
    current: {
      temp?: string
      description?: string
      icon?: string
    }
  } | null
}

export interface PlaceWeatherResponse {
  weather: PlaceWeather | null
}

export interface PlaceMenusResponse {
  menus: PlaceMenu[] | null
}

export interface GetRelatedPlaceMarkersWithMessageArgs {
  placeRef: PlaceRef
  messageId: Message["id"]
}

export interface SearchPlacesArgs {
  placeType: PlaceType | "all"
  searchString: string
  locationEntityRefs?: PlaceRef[]
  startDate?: DayObject
  endDate?: DayObject
  searchParent?: boolean
  multiCategory?: boolean
  currentPosition?: UserPosition
}

export interface WebSearchPlacesArgs {
  placeType: PlaceType | "all"
  searchString: string
  locationEntityRefs?: PlaceRef[]
  searchParent?: boolean
  sessionToken?: string
}

export interface ResolvePlaceArgs {
  placeType: PlaceType
  gpId: string
  sessionToken?: string
}

export interface GetPlaceRouteArgs {
  originEntityRef: PlaceRef
  destinationEntityRef: PlaceRef
  travelMode?: RouteTravelMode
}

export interface SampleQuestionsResponse {
  questions?: string[]
  questions_v2?: Array<{ title: string; text: string }>
}

export interface EventsResponse {
  data: EventSummary[]
  meta: PagingMeta
}

export interface PlaceReviewConfig {
  place_type: PlaceType
  rating_options: Array<{ rating: number; label: string }>
  highlights_by_rating: Record<string, string[]>
}

export const placeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPlace: builder.query<PlaceSummary, GetPlaceArgs>({
      query: ({ placeRef, eventParams }) => {
        let url = `/api/places/${placeRef}`
        if (eventParams) {
          const params = new URLSearchParams()
          if (eventParams.at !== undefined) {
            params.append("at", eventParams.at.toString())
          }
          if (params.toString()) {
            url += `?${params.toString()}`
          }
        }

        return {
          url,
          impressionIdHeader: "Impression-Id",
        }
      },
      transformResponse: (response: { place: PlaceSummary }) => response.place,
      providesTags: (result, _error, { placeRef }) =>
        result ? [{ type: "Place", id: placeRef }] : [],
    }),
    getDetailedPlace: builder.query<DetailedPlace, GetDetailedPlaceArgs>({
      query: ({ placeRef, eventParams }) => {
        let url = `/api/places/${placeRef}/detailed`
        if (eventParams) {
          const params = new URLSearchParams()
          if (eventParams.at !== undefined) {
            params.append("at", eventParams.at.toString())
          }
          if (params.toString()) {
            url += `?${params.toString()}`
          }
        }

        return {
          url,
          impressionIdHeader: "Impression-Id",
        }
      },
      transformResponse: (response: { place: DetailedPlace }) => response.place,
      providesTags: (result, _error, { placeRef }) =>
        result ? [{ type: "DetailedPlace", id: placeRef }] : [],
    }),
    getRelatedPlaceMarkers: builder.query<RelatedPlacePlaceholders, PlaceRef>({
      query: (placeRef) => {
        const url = `/api/places/${placeRef}/related_markers`

        return {
          url,
          impressionIdHeader: "Impression-Id",
        }
      },
      providesTags: (result, _error, placeRef) =>
        result ? [{ type: "RelatedPlaces", id: placeRef }] : [],
    }),
    getLocation: builder.query<LocationSummary, PlaceRef>({
      query: (placeRef) => {
        const url = `/api/places/${placeRef}`

        return {
          url,
          impressionIdHeader: "Impression-Id",
        }
      },
      transformResponse: (response: { place: LocationSummary }) =>
        response.place,
      providesTags: (result, _error, placeRef) =>
        result ? [{ type: "Location", id: placeRef }] : [],
    }),
    getPlacesByRef: builder.query<PlaceSummary[], PlaceRef[]>({
      queryFn: async (placeRefs, _queryApi, _extraOptions, baseQuery) => {
        const refChunks: PlaceRef[][] = []
        for (
          let i = 0;
          i < placeRefs.length;
          i += PLACES_BY_REF_REQUEST_LIMIT
        ) {
          refChunks.push(placeRefs.slice(i, i + PLACES_BY_REF_REQUEST_LIMIT))
        }

        const results = await Promise.all(
          refChunks.map(async (refs) =>
            baseQuery({
              url: `/api/places?entity_refs=${refs.join(",")}`,
              impressionIdHeader: "Impression-Id",
            })
          )
        )

        const places: PlaceSummary[] = []
        for (const result of results) {
          if (result.error) {
            return { error: result.error }
          }
          const response = result.data as { places: PlaceSummary[] }
          places.push(...response.places)
        }
        return { data: places }
      },
      providesTags: (result, _error, placeRefs) =>
        result
          ? placeRefs.map((ref) => ({ type: "Place" as const, id: ref }))
          : [],
    }),
    getSampleQuestions: builder.query<SampleQuestionsResponse, PlaceRef>({
      query: (placeRef) => {
        const url = `/api/places/${placeRef}/sample_questions`

        return {
          url,
          impressionIdHeader: "Impression-Id",
        }
      },
    }),
    getStats: builder.query<Stats, PlaceRef>({
      query: (placeRef) => {
        const url = `/api/places/${placeRef}/community_stats`

        return {
          url,
          impressionIdHeader: "Impression-Id",
        }
      },
      providesTags: (result, _error, placeRef) =>
        result ? [{ type: "Stats", id: placeRef }] : [],
    }),
    getCommunityFeed: builder.query<CommunityFeed, GetCommunityFeedArgs>({
      query: ({ placeRef, page = 1, perPage }) => {
        const params = new URLSearchParams()
        params.append("page", page.toString())
        if (perPage !== undefined) {
          params.append("per_page", perPage.toString())
        }
        const url = `/api/places/${placeRef}/community_feed?${params.toString()}`

        return {
          url,
          impressionIdHeader: "Impression-Id",
        }
      },
      serializeQueryArgs: ({ queryArgs }) => {
        return `${queryArgs.placeRef}:${queryArgs.perPage ?? PLACE_MENTIONS_PAGE_SIZE}`
      },
      merge: (current, incoming, { arg }) => {
        if (arg.page && arg.page > 1 && current) {
          const existingIds = new Set(current.data.map((item) => item.id))
          const newItems = incoming.data.filter(
            (item) => !existingIds.has(item.id)
          )
          return {
            ...incoming,
            data: [...current.data, ...newItems],
            meta: {
              ...incoming.meta,
              current_page: incoming.meta.current_page,
            },
          }
        }
        return incoming
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page
      },
      providesTags: (result, _error, { placeRef }) =>
        result ? [{ type: "CommunityFeed", id: placeRef }] : [],
    }),
    getCurrentUserPlaceReview: builder.query<
      { review: PlaceReview | null },
      { placeRef: PlaceRef }
    >({
      query: ({ placeRef }) => ({
        url: `/api/places/${placeRef}/review`,
      }),
      providesTags: (_result, _error, { placeRef }) => [
        { type: "CurrentUserPlaceReview", id: placeRef },
      ],
    }),
    getPlaceReviewConfig: builder.query<
      PlaceReviewConfig,
      { placeType: PlaceType }
    >({
      query: ({ placeType }) => ({
        url: "/api/place_reviews/config",
        params: { place_type: placeType },
      }),
      providesTags: (_result, _error, { placeType }) => [
        { type: "PlaceReviewConfig", id: placeType },
      ],
    }),
    createPlaceReview: builder.mutation<
      { review: PlaceReview },
      {
        placeRef: PlaceRef
        payload: Required<Pick<PlaceReviewPayload, "rating">> &
          PlaceReviewPayload
      }
    >({
      query: ({ placeRef, payload }) => ({
        url: `/api/places/${placeRef}/reviews`,
        method: "post",
        data: payload,
      }),
      invalidatesTags: (_result, _error, { placeRef }) => [
        { type: "CommunityFeed", id: placeRef },
        { type: "Stats", id: placeRef },
        { type: "PlaceReviews", id: "current" },
        { type: "CurrentUserPlaceReview", id: placeRef },
      ],
    }),
    updatePlaceReview: builder.mutation<
      { review: PlaceReview },
      { placeRef: PlaceRef; reviewId: number; payload: PlaceReviewPayload }
    >({
      query: ({ placeRef, reviewId, payload }) => ({
        url: `/api/places/${placeRef}/reviews/${reviewId}`,
        method: "put",
        data: payload,
      }),
      invalidatesTags: (_result, _error, { placeRef }) => [
        { type: "CommunityFeed", id: placeRef },
        { type: "Stats", id: placeRef },
        { type: "PlaceReviews", id: "current" },
        { type: "CurrentUserPlaceReview", id: placeRef },
      ],
    }),
    uploadPlaceReviewImage: builder.mutation<
      { url: string; width: number; height: number },
      { placeRef: PlaceRef; reviewId: number; file: File }
    >({
      query: ({ placeRef, reviewId, file }) => {
        const formData = new FormData()
        formData.append("file", file)
        return {
          url: `/api/places/${placeRef}/reviews/${reviewId}/upload_image`,
          method: "post",
          data: formData,
          headers: {
            "content-type": "multipart/form-data",
          },
        }
      },
      invalidatesTags: (_result, _error, { placeRef }) => [
        { type: "CommunityFeed", id: placeRef },
        { type: "PlaceReviews", id: "current" },
        { type: "CurrentUserPlaceReview", id: placeRef },
      ],
    }),
    deletePlaceReview: builder.mutation<
      void,
      { placeRef: PlaceRef; reviewId: number }
    >({
      query: ({ placeRef, reviewId }) => ({
        url: `/api/places/${placeRef}/reviews/${reviewId}`,
        method: "delete",
      }),
      invalidatesTags: (_result, _error, { placeRef }) => [
        { type: "CommunityFeed", id: placeRef },
        { type: "Stats", id: placeRef },
        { type: "PlaceReviews", id: "current" },
        { type: "CurrentUserPlaceReview", id: placeRef },
      ],
    }),
    getCurrentUserReviews: builder.query<
      { data: PlaceReview[]; meta: PagingMeta },
      GetUserReviewsArgs | void
    >({
      query: (args) => ({
        url: "/api/users/current/reviews",
        params: {
          page: args?.page ?? 1,
          per_page: args?.perPage,
        },
      }),
      providesTags: [{ type: "PlaceReviews", id: "current" }],
    }),
    getUserReviews: builder.query<
      { data: PlaceReview[]; meta: PagingMeta },
      Required<Pick<GetUserReviewsArgs, "handle">> & GetUserReviewsArgs
    >({
      query: ({ handle, page = 1, perPage }) => ({
        url: `/api/users/${handle}/reviews`,
        params: {
          page,
          per_page: perPage,
        },
      }),
      providesTags: (_result, _error, { handle }) => [
        { type: "PlaceReviews", id: handle },
      ],
    }),
    getEventsForPlace: builder.query<EventsResponse, GetEventsArgs>({
      query: ({ placeRef, eventParams }) => {
        const params = new URLSearchParams()
        if (eventParams?.page !== undefined) {
          params.append("page", eventParams.page.toString())
        }
        if (eventParams?.per_page !== undefined) {
          params.append("per_page", eventParams.per_page.toString())
        }
        if (eventParams?.at !== undefined) {
          params.append("at", eventParams.at.toString())
        }
        if (eventParams?.from !== undefined) {
          params.append("from", eventParams.from.toString())
        }
        if (eventParams?.to !== undefined) {
          params.append("to", eventParams.to.toString())
        }
        const url = `/api/places/${placeRef}/events?${params.toString()}`

        return {
          url,
          impressionIdHeader: "Impression-Id",
        }
      },
      transformResponse: (response: { events: EventsResponse }) =>
        response.events,
      providesTags: (result, _error, { placeRef }) =>
        result ? [{ type: "Events", id: placeRef }] : [],
    }),
    getDetailedPlaceWithMessage: builder.query<
      DetailedPlace,
      GetDetailedPlaceWithMessageArgs
    >({
      query: ({ placeRef, messageId, eventParams }) => {
        let url = `/api/messages/${messageId}/place/${placeRef}/detailed`
        if (eventParams) {
          const params = new URLSearchParams()
          if (eventParams.at !== undefined) {
            params.append("at", eventParams.at.toString())
          }
          if (params.toString()) {
            url += `?${params.toString()}`
          }
        }

        return {
          url,
          impressionIdHeader: "Impression-Id",
        }
      },
      transformResponse: (response: { place: DetailedPlace }) => response.place,
      providesTags: (result, _error, { placeRef }) =>
        result ? [{ type: "DetailedPlace", id: placeRef }] : [],
    }),
    getRelatedPlaceMarkersWithMessage: builder.query<
      RelatedPlacePlaceholders,
      GetRelatedPlaceMarkersWithMessageArgs
    >({
      query: ({ placeRef, messageId }) => {
        const url = `/api/messages/${messageId}/place/${placeRef}/related_markers`

        return {
          url,
          impressionIdHeader: "Impression-Id",
        }
      },
      providesTags: (result, _error, { placeRef }) =>
        result ? [{ type: "RelatedPlaces", id: placeRef }] : [],
    }),
    getMapWeather: builder.query<MapWeatherResponse, GetMapWeatherArgs>({
      query: ({ swLat, swLng, neLat, neLng, zoom }) => ({
        url: "/api/map/weather",
        params: {
          sw_lat: swLat,
          sw_lng: swLng,
          ne_lat: neLat,
          ne_lng: neLng,
          zoom,
        },
      }),
    }),
    getMapReferencePlaces: builder.query<
      MapReferencePlacesResponse,
      GetMapReferencePlacesArgs
    >({
      query: ({ swLat, swLng, neLat, neLng, types, orgid }) => ({
        url: "/api/map/reference-places",
        params: {
          sw_lat: swLat,
          sw_lng: swLng,
          ne_lat: neLat,
          ne_lng: neLng,
          ...(types?.length ? { types: types.join(",") } : {}),
          ...(orgid ? { orgid } : {}),
        },
      }),
    }),
    getPlaceWeather: builder.query<PlaceWeatherResponse, PlaceRef>({
      query: (placeRef) => {
        const url = `/api/places/${placeRef}/weather`

        return {
          url,
          impressionIdHeader: "Impression-Id",
        }
      },
    }),
    getPlaceMenus: builder.query<PlaceMenusResponse, PlaceRef>({
      query: (placeRef) => ({
        url: `/api/places/${placeRef}/menus`,
      }),
    }),
    searchPlaces: builder.mutation<
      PlaceTypeAheadSearchResult,
      SearchPlacesArgs
    >({
      query: (args) => {
        const url = "/api/places/search_type_ahead"

        return {
          url,
          method: "post",
          data: {
            place_type: args.placeType,
            search_string: args.searchString,
            multi_category: !!args.multiCategory,
            location_entity_refs: args.locationEntityRefs,
            search_parent: args.searchParent,
            start_date: args.startDate,
            end_date: args.endDate,
            exact_lat: args.currentPosition?.latitude,
            exact_long: args.currentPosition?.longitude,
          },
          impressionIdHeader: "Impression-Id",
        }
      },
    }),
    webSearchPlaces: builder.mutation<
      PlaceWebTypeAheadSearchResult,
      WebSearchPlacesArgs
    >({
      query: (args) => {
        const url = "/api/places/web_search_type_ahead"

        return {
          url,
          method: "post",
          data: {
            place_type: args.placeType,
            search_string: args.searchString,
            location_entity_refs: args.locationEntityRefs,
            search_parent: args.searchParent,
            session_token: args.sessionToken,
          },
          impressionIdHeader: "Impression-Id",
        }
      },
    }),
    resolvePlace: builder.mutation<PlaceSummary, ResolvePlaceArgs>({
      query: (args) => {
        const url = "/api/places/resolve_using_id"

        return {
          url,
          method: "post",
          data: {
            place_type: args.placeType,
            gp_id: args.gpId,
            session_token: args.sessionToken,
          },
          impressionIdHeader: "Impression-Id",
        }
      },
      transformResponse: (response: { place: PlaceSummary }) => response.place,
    }),
    getPlaceRoute: builder.mutation<Route | null, GetPlaceRouteArgs>({
      query: (args) => {
        const url = "/api/places/route"
        return {
          url,
          method: "post",
          data: {
            origin_entity_ref: args.originEntityRef,
            destination_entity_ref: args.destinationEntityRef,
            travel_mode: args.travelMode,
          },
        }
      },
      transformResponse: (response: { route?: Route | null }) =>
        response.route ?? null,
    }),
  }),
})

export const {
  useGetPlaceQuery,
  useLazyGetPlaceQuery,
  useGetDetailedPlaceQuery,
  useLazyGetDetailedPlaceQuery,
  useGetLocationQuery,
  useLazyGetLocationQuery,
  useGetPlacesByRefQuery,
  useLazyGetPlacesByRefQuery,
  useGetSampleQuestionsQuery,
  useLazyGetSampleQuestionsQuery,
  useGetStatsQuery,
  useLazyGetStatsQuery,
  useGetCommunityFeedQuery,
  useLazyGetCommunityFeedQuery,
  useGetCurrentUserPlaceReviewQuery,
  useGetPlaceReviewConfigQuery,
  useCreatePlaceReviewMutation,
  useUpdatePlaceReviewMutation,
  useUploadPlaceReviewImageMutation,
  useDeletePlaceReviewMutation,
  useGetCurrentUserReviewsQuery,
  useGetUserReviewsQuery,
  useGetEventsForPlaceQuery,
  useLazyGetEventsForPlaceQuery,
  useGetDetailedPlaceWithMessageQuery,
  useLazyGetDetailedPlaceWithMessageQuery,
  useLazyGetMapWeatherQuery,
  useLazyGetMapReferencePlacesQuery,
  useGetPlaceWeatherQuery,
  useLazyGetPlaceWeatherQuery,
  useLazyGetPlaceMenusQuery,
  useSearchPlacesMutation,
  useWebSearchPlacesMutation,
  useResolvePlaceMutation,
  useGetPlaceRouteMutation,
} = placeApi
